import { getClass } from '../shared/classes';
import { BIOME_RGB, biomeAt } from '../shared/biomes';
import { ENEMIES } from '../shared/enemies';
import { RARITIES } from '../shared/equipment';
import { CITY_CENTER, CITY_RADIUS, CITY_WALL_RADIUS, DROP_FADE_MS, PLAYER_RADIUS, WORLD_SIZE } from '../shared/constants';
import { TAU, clamp, damp, hash2 } from '../shared/math';
import type { LeaderboardEntry } from '../shared/protocol';
import type { NpcInfo, Obstacle, WorldInfo } from '../shared/types';
import {
  drawDrop,
  drawEnemyBody,
  drawGuard,
  drawHealthBar,
  drawMonument,
  drawObstacle,
  drawPlayerBody,
  drawQuestGiver,
  drawArmourer,
  drawSafeZone,
  drawTrader,
  drawWeaponsmith,
} from './sprites';
import type { ViewDrop, ViewEnemy, ViewPlayer, WorldView } from './view';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
  drag: number;
  glow: boolean;
}

export interface FloatText {
  x: number;
  y: number;
  vy: number;
  life: number;
  max: number;
  text: string;
  color: string;
  size: number;
}

export interface Ring {
  x: number;
  y: number;
  from: number;
  to: number;
  life: number;
  max: number;
  color: string;
  width: number;
}

export interface Slash {
  x: number;
  y: number;
  aim: number;
  arc: number;
  range: number;
  life: number;
  max: number;
  color: string;
}

export interface DrawOptions {
  selfId: number;
  zoom: number;
  mouse: { x: number; y: number };
  target: { x: number; y: number } | null;
  connecting: boolean;
}

const GROUND_TILE = 32;
const GROUND_SIZE = GROUND_TILE * 16;
/** Seconds of remaining lifetime at which loot starts blinking. */
const DROP_FADE_SECONDS = DROP_FADE_MS / 1000;

/**
 * The canvas renders at the display's own device pixel ratio, capped at 2.
 * Nothing here deliberately renders below native: an earlier attempt to cap the
 * backing store and scale it down adaptively made the game visibly soft on
 * HiDPI screens, which is not a trade worth making by default. A machine that
 * struggles has the explicit `G` low-graphics toggle instead.
 */
function chooseResolution(): number {
  return Math.min(2, window.devicePixelRatio || 1);
}

type Drawable =
  | { y: number; kind: 'obstacle'; ref: Obstacle }
  | { y: number; kind: 'player'; ref: ViewPlayer }
  | { y: number; kind: 'enemy'; ref: ViewEnemy }
  | { y: number; kind: 'trader'; ref: NpcInfo };

export class Renderer {
  readonly camera = { x: WORLD_SIZE / 2, y: WORLD_SIZE / 2, zoom: 1 };
  readonly particles: Particle[] = [];
  readonly texts: FloatText[] = [];
  readonly rings: Ring[] = [];
  readonly slashes: Slash[] = [];

  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private width = 0;
  private height = 0;
  private ground: CanvasPattern | null = null;
  private cobble: CanvasPattern | null = null;
  /** Low-resolution terrain colour map, built once and stretched over the world. */
  private biomeMap: HTMLCanvasElement | null = null;
  private minimapBase: HTMLCanvasElement | null = null;
  /** The vignette, baked to an image once per size: gradient fills are costly. */
  private vignetteCanvas: HTMLCanvasElement | null = null;
  /** Resolution of the display, before the adaptive scale below. */
  private baseDpr = 1;
  /** 0.6-1 adaptive render scale; dropped only when frames run long. */
  private scale = 1;
  /** When the minimap was last repainted; it does not need 60 Hz. */
  private minimapAt = 0;
  private time = 0;
  private shake = 0;

  /**
   * Firefox paints a 2D canvas much more slowly than Chromium, especially
   * gradient/pattern fills and `lighter` blending. When it looks like Firefox,
   * or the player turned on low graphics, we fall back to the cheap variants of
   * those layers. Everything gameplay-relevant still draws.
   */
  private readonly firefox =
    typeof navigator !== 'undefined' && /firefox|fxios/i.test(navigator.userAgent);

  private get cheapEffects(): boolean {
    return this.lowQuality || this.firefox;
  }

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas is not available in this browser.');
    this.ctx = ctx;
    this.buildBiomeMap();
    this.buildGround();
    this.buildCobble();
    this.resize();
  }

  /* -------------------------------------------------------------------- perf */

  /**
   * Low graphics mode. Cuts the layers that cost the most on a software canvas:
   * pattern fills for terrain, the full-screen vignette blend, and the dashed
   * range indicator. Everything gameplay-relevant still draws.
   */
  lowQuality = false;

  toggleQuality(): boolean {
    this.lowQuality = !this.lowQuality;
    return this.lowQuality;
  }

  /** Backing store size and the resolution scale, for the perf readout. */
  get canvasWidth(): number {
    return this.canvas.width;
  }

  get canvasHeight(): number {
    return this.canvas.height;
  }

  get pixelRatio(): number {
    return this.dpr;
  }

  resize(): void {
    this.baseDpr = chooseResolution();
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.applyBacking();
    // The baked vignette depends on the viewport size, so it is rebuilt lazily.
    this.vignetteCanvas = null;
  }

  /** Applies the current resolution scale to the backing store. */
  private applyBacking(): void {
    this.dpr = this.baseDpr * this.scale;
    this.canvas.width = Math.floor(this.width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
  }

  /**
   * Adaptive resolution. `1` is native (crisp); lower values shrink the backing
   * store and let the browser upscale, which is the single biggest lever when
   * the canvas is fill-rate bound (Firefox in particular).
   */
  setScale(scale: number): void {
    const next = clamp(scale, 0.6, 1);
    if (Math.abs(next - this.scale) < 0.01) return;
    this.scale = next;
    this.applyBacking();
  }

  get renderScale(): number {
    return this.scale;
  }

  /* ------------------------------------------------------------------ fx spawners */

  burst(
    x: number,
    y: number,
    color: string,
    count: number,
    options: { speed?: number; size?: number; life?: number; glow?: boolean; spread?: number; aim?: number } = {},
  ): void {
    const speed = options.speed ?? 130;
    const size = options.size ?? 3;
    const life = options.life ?? 0.5;
    const spread = options.spread ?? TAU;
    const base = options.aim ?? 0;
    // Bound the effect budget so overlapping bursts cannot grind the frame down.
    const MAX_PARTICLES = 420;
    if (this.particles.length + count > MAX_PARTICLES) {
      this.particles.splice(0, this.particles.length + count - MAX_PARTICLES);
    }
    for (let i = 0; i < count; i++) {
      const angle = spread >= TAU ? Math.random() * TAU : base + (Math.random() - 0.5) * spread;
      const v = speed * (0.4 + Math.random() * 0.8);
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * v,
        vy: Math.sin(angle) * v,
        life: life * (0.6 + Math.random() * 0.7),
        max: life,
        size: size * (0.6 + Math.random() * 0.8),
        color,
        drag: options.glow ? 2.6 : 5,
        glow: options.glow ?? false,
      });
    }
  }

  damageText(x: number, y: number, text: string, color: string, size = 15): void {
    this.texts.push({ x, y, vy: -46, life: 0.9, max: 0.9, text, color, size });
  }

  ring(x: number, y: number, from: number, to: number, color: string, life = 0.45, width = 3): void {
    this.rings.push({ x, y, from, to, life, max: life, color, width });
  }

  slash(x: number, y: number, aim: number, arc: number, range: number, color: string): void {
    this.slashes.push({ x, y, aim, arc, range, life: 0.22, max: 0.22, color });
  }

  /** The visual burst for a cast ability. Mechanics all live on the server. */
  castEffect(
    x: number,
    y: number,
    aim: number,
    radius: number,
    distance: number,
    colour: string,
    ability: string,
  ): void {
    switch (ability) {
      case 'whirlwind':
      case 'nova': {
        this.ring(x, y, 18, radius, colour, 0.6, 8);
        this.ring(x, y, 6, radius * 0.72, '#ffffff', 0.42, 3);
        this.slash(x, y, aim, Math.PI * 2, radius * 0.95, colour);
        this.slash(x, y, aim - 1.3, 2.6, radius * 0.78, 'rgba(255,255,255,0.9)');
        this.burst(x, y, colour, 30, { speed: 340, size: 4.2, life: 0.62, glow: true });
        break;
      }
      case 'volley': {
        const mx = x + Math.cos(aim) * 26;
        const my = y + Math.sin(aim) * 26;
        this.burst(mx, my, colour, 14, { speed: 260, size: 3, life: 0.34, glow: true, aim, spread: 0.9 });
        break;
      }
      case 'pierce': {
        const mx = x + Math.cos(aim) * 26;
        const my = y + Math.sin(aim) * 26;
        this.burst(mx, my, colour, 16, { speed: 300, size: 3.4, life: 0.3, glow: true, aim, spread: 0.3 });
        break;
      }
      case 'charge': {
        // Dust kicked up along the path the charge covered.
        const steps = Math.max(3, Math.round(distance / 70));
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          this.burst(x + Math.cos(aim) * distance * t, y + Math.sin(aim) * distance * t, colour, 6, {
            speed: 90,
            size: 5,
            life: 0.45,
            glow: false,
          });
        }
        break;
      }
      case 'blink': {
        const ex = x + Math.cos(aim) * distance;
        const ey = y + Math.sin(aim) * distance;
        this.ring(x, y, 4, 44, colour, 0.4, 4);
        this.ring(ex, ey, 4, 52, colour, 0.45, 4);
        this.burst(x, y, colour, 18, { speed: 190, size: 3.4, life: 0.42, glow: true });
        this.burst(ex, ey, '#ffffff', 14, { speed: 210, size: 3, life: 0.36, glow: true });
        break;
      }
    }
  }

  shakeCamera(amount: number): void {
    this.shake = Math.min(14, this.shake + amount);
  }

  /* -------------------------------------------------------------------- camera */

  follow(x: number, y: number, dt: number, snap = false): void {
    if (snap) {
      this.camera.x = x;
      this.camera.y = y;
    } else {
      const k = damp(7, dt);
      this.camera.x += (x - this.camera.x) * k;
      this.camera.y += (y - this.camera.y) * k;
    }
    const halfW = this.width / (2 * this.camera.zoom);
    const halfH = this.height / (2 * this.camera.zoom);
    this.camera.x = halfW * 2 >= WORLD_SIZE ? WORLD_SIZE / 2 : clamp(this.camera.x, halfW * 0.92, WORLD_SIZE - halfW * 0.92);
    this.camera.y = halfH * 2 >= WORLD_SIZE ? WORLD_SIZE / 2 : clamp(this.camera.y, halfH * 0.92, WORLD_SIZE - halfH * 0.92);
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return {
      x: this.camera.x + (sx - this.width / 2) / this.camera.zoom,
      y: this.camera.y + (sy - this.height / 2) / this.camera.zoom,
    };
  }

  visibleRect(padding: number): { x0: number; y0: number; x1: number; y1: number } {
    const halfW = this.width / (2 * this.camera.zoom) + padding;
    const halfH = this.height / (2 * this.camera.zoom) + padding;
    return {
      x0: this.camera.x - halfW,
      y0: this.camera.y - halfH,
      x1: this.camera.x + halfW,
      y1: this.camera.y + halfH,
    };
  }

  /* ---------------------------------------------------------------------- draw */

  draw(view: WorldView, dt: number, options: DrawOptions): void {
    this.time += dt;
    this.camera.zoom = options.zoom;
    this.partyIds = view.partyIds;
    this.leaderboard = view.leaderboard;
    this.shake = Math.max(0, this.shake - dt * 34);

    const ctx = this.ctx;
    const world = view.world;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    if (!world) {
      ctx.fillStyle = '#070b11';
      ctx.fillRect(0, 0, this.width, this.height);
      return;
    }

    // Only clear the void when the terrain might not cover the viewport (near a
    // world edge). Everywhere else drawGround fills every pixel, so the extra
    // full-screen pass is wasted - and full-screen passes are what Firefox
    // struggles with.
    const halfW = this.width / (2 * this.camera.zoom);
    const halfH = this.height / (2 * this.camera.zoom);
    const pad = 160;
    const insideWorld =
      this.camera.x - halfW - pad >= 0 &&
      this.camera.y - halfH - pad >= 0 &&
      this.camera.x + halfW + pad <= WORLD_SIZE &&
      this.camera.y + halfH + pad <= WORLD_SIZE;
    if (!insideWorld) {
      ctx.fillStyle = '#070b11';
      ctx.fillRect(0, 0, this.width, this.height);
    }

    const shakeX = (Math.random() * 2 - 1) * this.shake;
    const shakeY = (Math.random() * 2 - 1) * this.shake;

    ctx.save();
    ctx.translate(this.width / 2 + shakeX, this.height / 2 + shakeY);
    ctx.scale(this.camera.zoom, this.camera.zoom);
    ctx.translate(-this.camera.x, -this.camera.y);

    const view_ = this.visibleRect(140);
    this.drawGround(view_);
    this.drawBorder();
    // The sanctuary ring is a long dashed circle; skip it when off screen.
    if (this.ringVisible(world.safeZone.x, world.safeZone.y, world.safeZone.r)) {
      drawSafeZone(ctx, world.safeZone.x, world.safeZone.y, world.safeZone.r, this.time);
    }

    // Loot lies flat on the ground, under everything that stands on it.
    for (const drop of view.drops.values()) {
      if (drop.x < view_.x0 || drop.x > view_.x1 || drop.y < view_.y0 || drop.y > view_.y1) continue;
      ctx.save();
      ctx.translate(drop.x, drop.y);
      const scale = 1 + drop.pop * 0.5;
      ctx.scale(scale, scale);
      // Blink out as the two minutes run down, so nothing vanishes unannounced.
      if (drop.snap.ttl <= DROP_FADE_SECONDS) {
        const urgency = 1 - drop.snap.ttl / DROP_FADE_SECONDS;
        ctx.globalAlpha = 0.45 + 0.55 * Math.abs(Math.cos(this.time * (3 + urgency * 9)));
      }
      const rarityColor = drop.snap.gear ? RARITIES[drop.snap.gear.rarity].color : undefined;
      drawDrop(ctx, drop.snap.kind, drop.snap.amount, this.time, drop.x * 0.05, rarityColor);
      ctx.restore();
    }

    // Build a y-sorted list so entities overlap obstacles convincingly.
    const drawables: Drawable[] = [];
    for (const obstacle of view.obstacles) {
      if (obstacle.x < view_.x0 - obstacle.r || obstacle.x > view_.x1 + obstacle.r) continue;
      if (obstacle.y < view_.y0 - obstacle.r || obstacle.y > view_.y1 + obstacle.r) continue;
      drawables.push({ y: obstacle.y, kind: 'obstacle', ref: obstacle });
    }
    for (const npc of world.npcs) {
      drawables.push({ y: npc.y, kind: 'trader', ref: npc });
    }
    for (const enemy of view.enemies.values()) {
      if (enemy.x < view_.x0 || enemy.x > view_.x1 || enemy.y < view_.y0 || enemy.y > view_.y1) continue;
      drawables.push({ y: enemy.y, kind: 'enemy', ref: enemy });
    }
    for (const player of view.players.values()) {
      if (player.x < view_.x0 || player.x > view_.x1 || player.y < view_.y0 || player.y > view_.y1) continue;
      drawables.push({ y: player.y, kind: 'player', ref: player });
    }
    drawables.sort((a, b) => a.y - b.y);

    this.drawRangeIndicator(view, options.selfId);

    for (const item of drawables) {
      if (item.kind === 'obstacle') {
        ctx.save();
        ctx.translate(item.ref.x, item.ref.y);
        drawObstacle(ctx, item.ref, this.time);
        ctx.restore();
      } else if (item.kind === 'enemy') {
        this.drawEnemy(item.ref, options);
      } else if (item.kind === 'trader') {
        this.drawNpc(item.ref);
      } else {
        this.drawPlayer(item.ref, options);
      }
    }

    this.drawProjectiles(view);
    this.drawEffects();

    ctx.restore();

    this.drawOverlay(view, options);
  }

  /* ------------------------------------------------------------------- layers */

  private drawGround(view_: { x0: number; y0: number; x1: number; y1: number }): void {
    const ctx = this.ctx;
    const x0 = Math.max(0, view_.x0);
    const y0 = Math.max(0, view_.y0);
    const x1 = Math.min(WORLD_SIZE, view_.x1);
    const y1 = Math.min(WORLD_SIZE, view_.y1);
    if (x1 <= x0 || y1 <= y0) return;

    // Cobbled pavement inside the hub city. Both the clip and the kerb stroke
    // are expensive in Firefox, so each is skipped when it cannot be seen.
    const cityLeft = CITY_CENTER.x - CITY_RADIUS;
    const cityTop = CITY_CENTER.y - CITY_RADIUS;
    const citySize = CITY_RADIUS * 2;
    const cityOnScreen =
      cityLeft < x1 && cityLeft + citySize > x0 && cityTop < y1 && cityTop + citySize > y0;

    const toCentre = Math.hypot(this.camera.x - CITY_CENTER.x, this.camera.y - CITY_CENTER.y);
    const halfDiagonal = Math.hypot(this.width, this.height) / (2 * this.camera.zoom);
    const insideCity = cityOnScreen && toCentre + halfDiagonal < CITY_RADIUS;

    // Standing inside the city, the pavement covers the whole viewport, so the
    // wilderness ground underneath would be a wasted full-screen fill.
    // In low quality the repeating patterns are replaced by flat colour: a
    // pattern fill under a scaled transform is one of the costliest things a
    // software canvas can be asked to do.
    if (insideCity) {
      ctx.fillStyle = this.lowQuality ? '#393c44' : this.cobble ?? '#3c3f47';
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
    } else if (this.biomeMap) {
      // The terrain map is tiny (512px across the whole world) and stretched
      // over the viewport, so the biome edges blend smoothly for free. The
      // neutral grain pattern is laid over it for texture in full quality.
      const k = this.biomeMap.width / WORLD_SIZE;
      ctx.drawImage(this.biomeMap, x0 * k, y0 * k, (x1 - x0) * k, (y1 - y0) * k, x0, y0, x1 - x0, y1 - y0);
      // A pattern fill under a scaled transform is about the costliest thing a
      // software canvas can be asked to do, so it is the first thing dropped on
      // Firefox and in low graphics mode.
      if (!this.cheapEffects && this.ground) {
        ctx.fillStyle = this.ground;
        ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
      }
    } else {
      ctx.fillStyle = this.lowQuality ? '#1f2b20' : '#22301f';
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
    }

    if (cityOnScreen && !insideCity && !this.lowQuality) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(CITY_CENTER.x, CITY_CENTER.y, CITY_RADIUS, 0, TAU);
      ctx.clip();
      ctx.fillStyle = this.cobble ?? '#3c3f47';
      ctx.fillRect(cityLeft, cityTop, citySize, citySize);
      ctx.restore();
    }

    if (cityOnScreen && !this.lowQuality && Math.abs(toCentre - CITY_RADIUS) < halfDiagonal + 24) {
      ctx.save();
      ctx.strokeStyle = 'rgba(24, 28, 36, 0.55)';
      ctx.lineWidth = 14;
      ctx.beginPath();
      ctx.arc(CITY_CENTER.x, CITY_CENTER.y, CITY_RADIUS, 0, TAU);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(150, 160, 180, 0.16)';
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(CITY_CENTER.x, CITY_CENTER.y, CITY_RADIUS - 9, 0, TAU);
      ctx.stroke();
      ctx.restore();
    }
  }

  /**
   * The world border is a dashed rectangle 200,000 units around; building that
   * path costs thousands of dash segments every frame even when every edge is
   * far off screen. Skipped unless an edge is actually in view.
   */
  private drawBorder(): void {
    const view_ = this.visibleRect(0);
    if (view_.x0 > 0 && view_.y0 > 0 && view_.x1 < WORLD_SIZE && view_.y1 < WORLD_SIZE) return;

    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = 'rgba(120, 150, 190, 0.25)';
    ctx.lineWidth = 6;
    ctx.setLineDash([26, 18]);
    ctx.strokeRect(0, 0, WORLD_SIZE, WORLD_SIZE);
    ctx.restore();
  }

  /** True when part of the sanctuary ring could be on screen. */
  private ringVisible(x: number, y: number, radius: number): boolean {
    const halfDiagonal = Math.hypot(this.width, this.height) / (2 * this.camera.zoom);
    const toCentre = Math.hypot(this.camera.x - x, this.camera.y - y);
    return Math.abs(toCentre - radius) < halfDiagonal + 40;
  }

  /**
   * The vignette, baked once into a half-resolution image. A radial gradient
   * fill is evaluated per pixel every frame; blitting a pre-rendered image is
   * far cheaper, and half resolution is more than enough for a soft darkening
   * at the edges.
   */
  private getVignetteCanvas(): HTMLCanvasElement | null {
    if (this.vignetteCanvas) return this.vignetteCanvas;
    const w = Math.max(2, Math.floor(this.width / 2));
    const h = Math.max(2, Math.floor(this.height / 2));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const gradient = ctx.createRadialGradient(
      w / 2,
      h / 2,
      Math.min(w, h) * 0.34,
      w / 2,
      h / 2,
      Math.max(w, h) * 0.78,
    );
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.55)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);
    this.vignetteCanvas = canvas;
    return canvas;
  }

  private drawRangeIndicator(view: WorldView, selfId: number): void {
    const me = view.players.get(selfId);
    if (!me || me.snap.state !== 'alive') return;
    // A dashed circle of several hundred units; skipped when not wanted.
    if (this.lowQuality) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(me.x, me.y);
    ctx.strokeStyle = 'rgba(200, 220, 255, 0.07)';
    ctx.lineWidth = 2;
    ctx.setLineDash([9, 11]);
    ctx.beginPath();
    ctx.arc(0, 0, me.snap.range, 0, TAU);
    ctx.stroke();
    ctx.restore();
  }

  private drawNpc(npc: NpcInfo): void {
    const ctx = this.ctx;
    const height = npc.role === 'guard' ? 21 : npc.role === 'monument' ? 34 : 20;
    ctx.save();
    ctx.translate(npc.x, npc.y);
    if (npc.role === 'guard') drawGuard(ctx, this.time, height);
    else if (npc.role === 'questgiver') drawQuestGiver(ctx, this.time, height);
    else if (npc.role === 'monument') drawMonument(ctx, this.time, height, this.leaderboard);
    else if (npc.role === 'weaponeer') drawWeaponsmith(ctx, this.time, height);
    else if (npc.role === 'armourer') drawArmourer(ctx, this.time, height);
    else drawTrader(ctx, this.time, height);
    ctx.restore();

    const scale = 1 / this.camera.zoom;
    const top = npc.y - (npc.role === 'monument' ? height * 9.4 : height * 3.6);
    const nameColour =
      npc.role === 'guard'
        ? '#9fd0ff'
        : npc.role === 'questgiver'
          ? '#b8f0a0'
          : npc.role === 'monument'
            ? '#ffe9a8'
            : npc.role === 'weaponeer'
              ? '#cbd6e6'
              : npc.role === 'armourer'
                ? '#c7cede'
                : '#e5b95c';
    const roleLabel =
      npc.role === 'guard'
        ? 'Gate Guard'
        : npc.role === 'questgiver'
          ? 'Bounty Officer'
          : npc.role === 'monument'
            ? 'Leaderboard'
            : npc.role === 'weaponeer'
              ? 'Weaponsmith'
              : npc.role === 'armourer'
                ? 'Armorer'
                : 'Trader';

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.font = `700 ${12.5 * scale}px 'Segoe UI', system-ui, sans-serif`;
    ctx.lineWidth = 3 * scale;
    ctx.strokeStyle = 'rgba(0,0,0,0.75)';
    ctx.strokeText(npc.name, npc.x, top);
    ctx.fillStyle = nameColour;
    ctx.fillText(npc.name, npc.x, top);

    ctx.font = `600 ${10 * scale}px 'Segoe UI', system-ui, sans-serif`;
    ctx.strokeText(roleLabel, npc.x, top + 13 * scale);
    ctx.fillStyle = '#c8d3e2';
    ctx.fillText(roleLabel, npc.x, top + 13 * scale);
    ctx.restore();

    // Whatever they are muttering at you right now.
    const bubble = this.npcBubbles.get(npc.id);
    if (bubble) {
      if (performance.now() > bubble.until) {
        this.npcBubbles.delete(npc.id);
      } else {
        this.drawBubble(npc.x, top - 18 * scale, bubble.text, scale, nameColour);
      }
    }
  }

  /** Set by the caller each frame from the social state. */
  partyIds = new Set<number>();

  /** Transient speech bubbles above townsfolk, keyed by NPC id. */
  private npcBubbles = new Map<number, { text: string; until: number }>();

  /** Names currently carved on the monument. */
  private leaderboard: LeaderboardEntry[] = [];

  /** Shows a line above an NPC for a few seconds. */
  say(npcId: number, text: string, seconds = 5): void {
    this.npcBubbles.set(npcId, { text, until: performance.now() + seconds * 1000 });
  }

  private drawPlayer(view: ViewPlayer, options: DrawOptions): void {
    const ctx = this.ctx;
    const dead = view.snap.state === 'dead';
    ctx.save();
    ctx.translate(view.x, view.y);
    drawPlayerBody(
      ctx,
      view.snap.cls,
      view.aim,
      view.anim,
      PLAYER_RADIUS,
      { swing: view.swing, moving: view.moving, flash: view.hitFlash, alpha: dead ? 0.34 : 1 },
    );
    ctx.restore();

    const scale = 1 / this.camera.zoom;
    const top = view.y - PLAYER_RADIUS * 3.4;
    const isSelf = view.id === options.selfId;
    const cls = getClass(view.snap.cls);

    if (!dead && (view.snap.hp < view.snap.maxHp || isSelf)) {
      drawHealthBar(ctx, view.x, top, 40 * scale, view.snap.hp / view.snap.maxHp, { height: 5 * scale });
    }

    ctx.save();
    ctx.font = `700 ${12 * scale}px 'Segoe UI', system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.lineWidth = 3 * scale;
    ctx.strokeStyle = 'rgba(0,0,0,0.72)';
    const inParty = this.partyIds.has(view.id);
    const label = `${inParty ? '\u2726 ' : ''}${view.snap.name} · ${view.snap.level}`;
    const nameY = top - 5 * scale;
    ctx.strokeText(label, view.x, nameY);
    ctx.fillStyle = isSelf ? '#ffe9a8' : inParty ? '#8ee08a' : cls.accent;
    ctx.fillText(label, view.x, nameY);
    ctx.restore();

    // Chat bubble
    if (view.snap.bubble) {
      this.drawBubble(view.x, top - 20 * scale, view.snap.bubble, scale, cls.color);
    }
  }

  private drawEnemy(view: ViewEnemy, options: DrawOptions): void {
    const ctx = this.ctx;
    const snap = view.snap;
    const scale = 1 / this.camera.zoom;
    const elite = snap.elite === true;
    const radius = ENEMIES[snap.kind].radius;

    ctx.save();
    ctx.translate(view.x, view.y);
    // Elites wear a pulsing gold aura and are drawn a touch larger.
    if (elite) {
      const pulse = 0.5 + 0.5 * Math.sin(this.time * 3 + view.y * 0.01);
      ctx.strokeStyle = `rgba(255, 200, 90, ${0.3 + 0.4 * pulse})`;
      ctx.lineWidth = 2.5 * scale;
      ctx.beginPath();
      ctx.arc(0, 0, radius * 1.4, 0, TAU);
      ctx.stroke();
    }
    drawEnemyBody(ctx, snap.kind, view.aim, view.anim, elite ? radius * 1.08 : radius, {
      swing: 0,
      moving: view.moving,
      flash: view.hitFlash,
      alpha: 1,
      attacking: snap.attacking,
    });
    ctx.restore();

    const top = view.y - radius * 3.1;

    // Telegraph ring while the monster winds up an attack.
    if (snap.attacking && snap.windup > 0) {
      ctx.save();
      ctx.strokeStyle = `rgba(255, 120, 90, ${0.35 + 0.4 * Math.sin(this.time * 26)})`;
      ctx.lineWidth = 2.5 * scale;
      ctx.beginPath();
      ctx.arc(view.x, view.y, radius * 1.7, 0, TAU);
      ctx.stroke();
      ctx.restore();
    }

    // Bosses always show a fat health bar; ordinary monsters only once hurt.
    const boss = snap.boss === true;
    if (boss || snap.hp < snap.maxHp || view.hitFlash > 0) {
      drawHealthBar(ctx, view.x, top, (boss ? 74 : 34) * scale, snap.hp / snap.maxHp, {
        height: (boss ? 7 : 4) * scale,
      });
    }

    ctx.save();
    ctx.font = `${boss || elite ? 700 : 600} ${(boss ? 14 : 11) * scale}px 'Segoe UI', system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.lineWidth = 3 * scale;
    ctx.strokeStyle = 'rgba(0,0,0,0.75)';
    const label = `${snap.name} ${snap.level}`;
    ctx.strokeText(label, view.x, top - 5 * scale);
    ctx.fillStyle = boss ? '#ffd166' : elite ? '#ffcf7a' : snap.hp < snap.maxHp ? '#e8b0a6' : '#c8d3e2';
    ctx.fillText(label, view.x, top - 5 * scale);
    ctx.restore();
  }

  /**
   * Speech bubble above an entity. Wraps onto as many lines as the text needs
   * and sizes the box to fit, so long lines are readable rather than clipped.
   */
  private drawBubble(x: number, y: number, text: string, scale: number, color: string): void {
    const ctx = this.ctx;
    const fontSize = 12 * scale;
    const lineHeight = fontSize * 1.3;
    const padX = 9 * scale;
    const padY = 6 * scale;
    const maxWidth = 190 * scale;

    ctx.save();
    ctx.font = `600 ${fontSize}px 'Segoe UI', system-ui, sans-serif`;

    // Break the text on words so it stays inside maxWidth.
    const lines: string[] = [];
    let current = '';
    for (const word of text.split(' ')) {
      const candidate = current ? `${current} ${word}` : word;
      if (current && ctx.measureText(candidate).width > maxWidth) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) lines.push(current);

    let widest = 0;
    for (const line of lines) widest = Math.max(widest, ctx.measureText(line).width);
    const width = widest + padX * 2;
    const height = lines.length * lineHeight + padY * 2;
    const left = x - width / 2;
    const top = y - height;

    ctx.fillStyle = 'rgba(12, 18, 28, 0.92)';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5 * scale;
    ctx.beginPath();
    ctx.roundRect(left, top, width, height, 7 * scale);
    ctx.fill();
    ctx.stroke();

    // Tail pointing down at the speaker.
    ctx.beginPath();
    ctx.moveTo(x - 5 * scale, y);
    ctx.lineTo(x + 5 * scale, y);
    ctx.lineTo(x, y + 6 * scale);
    ctx.closePath();
    ctx.fillStyle = 'rgba(12, 18, 28, 0.92)';
    ctx.fill();

    ctx.fillStyle = '#e6edf8';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], x, top + padY + lineHeight * (i + 0.5));
    }
    ctx.restore();
  }

  private drawProjectiles(view: WorldView): void {
    const ctx = this.ctx;
    ctx.save();
    // Additive blending is a slow path on Firefox; normal blending keeps the
    // bolts readable at a fraction of the cost.
    ctx.globalCompositeOperation = this.cheapEffects ? 'source-over' : 'lighter';
    for (const shot of view.projectiles.values()) {
      const size = shot.snap.radius;
      const tail = 1 + size * 0.5;
      for (let i = 0; i < 4; i++) {
        const t = i / 4;
        const alpha = (1 - t) * 0.5;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = shot.snap.color;
        ctx.beginPath();
        ctx.arc(
          shot.x - Math.cos(shot.snap.aim) * tail * i * 3,
          shot.y - Math.sin(shot.snap.aim) * tail * i * 3,
          size * (1 - t * 0.6),
          0,
          TAU,
        );
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(shot.x, shot.y, size * 0.5, 0, TAU);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawEffects(): void {
    const ctx = this.ctx;
    const scale = 1 / this.camera.zoom;

    // Melee arcs
    ctx.save();
    ctx.globalCompositeOperation = this.cheapEffects ? 'source-over' : 'lighter';
    for (const slash of this.slashes) {
      const t = slash.life / slash.max;
      ctx.globalAlpha = t * 0.55;
      ctx.strokeStyle = slash.color;
      ctx.lineWidth = 16 * t * scale;
      ctx.beginPath();
      ctx.arc(slash.x, slash.y, slash.range * 0.82, slash.aim - slash.arc / 2, slash.aim + slash.arc / 2);
      ctx.stroke();
    }

    for (const ring of this.rings) {
      const t = 1 - ring.life / ring.max;
      ctx.globalAlpha = (1 - t) * 0.8;
      ctx.strokeStyle = ring.color;
      ctx.lineWidth = ring.width * (1 - t) * scale;
      ctx.beginPath();
      ctx.arc(ring.x, ring.y, ring.from + (ring.to - ring.from) * t, 0, TAU);
      ctx.stroke();
    }

    for (const particle of this.particles) {
      const t = Math.max(0, particle.life / particle.max);
      ctx.globalAlpha = t;
      ctx.fillStyle = particle.color;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size * t, 0, TAU);
      ctx.fill();
    }
    ctx.restore();

    // Floating combat text
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const text of this.texts) {
      const t = Math.max(0, text.life / text.max);
      ctx.globalAlpha = Math.min(1, t * 1.8);
      ctx.font = `800 ${text.size * scale}px 'Segoe UI', system-ui, sans-serif`;
      // Text is slow in Firefox; the outline doubles the cost, so it is skipped
      // in the cheap path (a dark shadow is unnecessary on the bright numbers).
      if (!this.cheapEffects) {
        ctx.lineWidth = 3.5 * scale;
        ctx.strokeStyle = 'rgba(0,0,0,0.8)';
        ctx.strokeText(text.text, text.x, text.y);
      }
      ctx.fillStyle = text.color;
      ctx.fillText(text.text, text.x, text.y);
    }
    ctx.restore();
  }

  private drawOverlay(view: WorldView, options: DrawOptions): void {
    const ctx = this.ctx;

    // Vignette, blitted from a baked image rather than evaluated as a gradient
    // every frame. The old centre light-pool gradient is gone: it was a 5%
    // highlight, invisible in practice, and one full-screen pass.
    if (!this.lowQuality) {
      const vignette = this.getVignetteCanvas();
      if (vignette) ctx.drawImage(vignette, 0, 0, this.width, this.height);
    }

    if (options.connecting) return;

    // Reticle on the tracked target
    if (options.target) {
      const { x, y } = options.target;
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 90, 80, 0.85)';
      ctx.lineWidth = 2;
      const r = 20;
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * TAU + Math.PI / 4;
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
        ctx.lineTo(x + Math.cos(a) * (r + 8), y + Math.sin(a) * (r + 8));
        ctx.stroke();
      }
      ctx.restore();
    }

    // Quest waypoint, under the crosshair so it never swallows aim feedback.
    this.drawQuestPointer(view);

    // Crosshair
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(options.mouse.x, options.mouse.y, 9, 0, TAU);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(options.mouse.x - 14, options.mouse.y);
    ctx.lineTo(options.mouse.x - 4, options.mouse.y);
    ctx.moveTo(options.mouse.x + 4, options.mouse.y);
    ctx.lineTo(options.mouse.x + 14, options.mouse.y);
    ctx.moveTo(options.mouse.x, options.mouse.y - 14);
    ctx.lineTo(options.mouse.x, options.mouse.y - 4);
    ctx.moveTo(options.mouse.x, options.mouse.y + 4);
    ctx.lineTo(options.mouse.x, options.mouse.y + 14);
    ctx.stroke();
    ctx.restore();

    const me = view.me;
    if (me && me.snap.state === 'dead') {
      ctx.save();
      ctx.fillStyle = 'rgba(60, 0, 0, 0.28)';
      ctx.fillRect(0, 0, this.width, this.height);
      ctx.restore();
    }
  }

  /**
   * A gold arrow near the screen edge pointing at the tracked bounty's
   * landmark, with the name and distance. Works whether the target is on
   * screen or far off it.
   */
  private drawQuestPointer(view: WorldView): void {
    const target = view.navTarget;
    if (!target) return;

    const dx = target.x - this.camera.x;
    const dy = target.y - this.camera.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 80) return; // Already standing on it.

    const angle = Math.atan2(dy, dx);
    const cx = this.width / 2;
    const cy = this.height / 2;
    const radius = Math.min(this.width, this.height) * 0.34;
    const px = cx + Math.cos(angle) * radius;
    const py = cy + Math.sin(angle) * radius;

    const ctx = this.ctx;
    ctx.save();
    ctx.translate(px, py);

    const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, 32);
    glow.addColorStop(0, 'rgba(255, 210, 110, 0.35)');
    glow.addColorStop(1, 'rgba(255, 210, 110, 0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, 32, 0, TAU);
    ctx.fill();

    ctx.rotate(angle);
    ctx.fillStyle = '#ffd166';
    ctx.strokeStyle = 'rgba(40, 28, 8, 0.85)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(18, 0);
    ctx.lineTo(-10, -12);
    ctx.lineTo(-4, 0);
    ctx.lineTo(-10, 12);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.font = "700 12px 'Segoe UI', system-ui, sans-serif";
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
    const label = `${target.name} - ${Math.round(dist)}m`;
    const labelY = clamp(py + 26, 12, this.height - 12);
    ctx.strokeText(label, clamp(px, 60, this.width - 60), labelY);
    ctx.fillStyle = '#ffe9a8';
    ctx.fillText(label, clamp(px, 60, this.width - 60), labelY);
    ctx.restore();
  }

  /* -------------------------------------------------------------------- minimap */

  buildMinimapBase(world: WorldInfo, obstacles: Obstacle[]): void {
    const size = 1500;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const k = size / world.width;

    const bg = ctx.createLinearGradient(0, 0, size, size);
    bg.addColorStop(0, '#101c23');
    bg.addColorStop(1, '#0c1418');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, size, size);

    // Faint biome colouring so the minimap shows where the regions lie.
    if (this.biomeMap) {
      ctx.globalAlpha = 0.55;
      ctx.drawImage(this.biomeMap, 0, 0, size, size);
      ctx.globalAlpha = 1;
    }

    ctx.fillStyle = 'rgba(110, 138, 112, 0.5)';
    for (const o of obstacles) {
      const city = o.kind !== 'rock' && o.kind !== 'tree';
      ctx.fillStyle = city ? 'rgba(190, 200, 215, 0.85)' : 'rgba(110, 138, 112, 0.5)';
      ctx.fillRect(o.x * k, o.y * k, 1.4, 1.4);
    }

    // The hub city, called out so it is easy to find your way home.
    ctx.beginPath();
    ctx.arc(CITY_CENTER.x * k, CITY_CENTER.y * k, CITY_WALL_RADIUS * k, 0, TAU);
    ctx.fillStyle = 'rgba(229, 185, 92, 0.2)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(229, 185, 92, 0.9)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.strokeStyle = 'rgba(120, 160, 200, 0.4)';
    ctx.lineWidth = 2;
    ctx.strokeRect(0.5, 0.5, size - 1, size - 1);

    this.minimapBase = canvas;
  }

  /**
   * The world is far too large to show whole, so the minimap is a window
   * centred on the player, blitted out of the pre-rendered world texture.
   */
  drawMinimap(canvas: HTMLCanvasElement, view: WorldView): void {
    const ctx = canvas.getContext('2d');
    const world = view.world;
    if (!ctx || !world) return;

    // The minimap is a scaled blit plus dots; ten times a second is plenty and
    // saves a per-frame rescale, which is not free on Firefox.
    const now = performance.now();
    if (now - this.minimapAt < 100) return;
    this.minimapAt = now;

    const size = canvas.width;
    ctx.clearRect(0, 0, size, size);

    const me = view.me;
    const span = 7000;
    const half = span / 2;
    const cx = clamp(me ? me.x : world.width / 2, half, world.width - half);
    const cy = clamp(me ? me.y : world.height / 2, half, world.height - half);

    if (this.minimapBase) {
      const srcScale = this.minimapBase.width / world.width;
      ctx.drawImage(
        this.minimapBase,
        (cx - half) * srcScale,
        (cy - half) * srcScale,
        span * srcScale,
        span * srcScale,
        0,
        0,
        size,
        size,
      );
    }

    const toMap = (x: number, y: number): [number, number] => [
      ((x - (cx - half)) / span) * size,
      ((y - (cy - half)) / span) * size,
    ];

    ctx.save();
    for (const enemy of view.enemies.values()) {
      const [mx, my] = toMap(enemy.x, enemy.y);
      if (mx < 0 || my < 0 || mx > size || my > size) continue;
      ctx.fillStyle = enemy.snap.attacking ? '#ff6b5a' : 'rgba(220, 90, 80, 0.85)';
      ctx.beginPath();
      ctx.arc(mx, my, 2, 0, TAU);
      ctx.fill();
    }
    for (const player of view.players.values()) {
      if (player.id === view.selfId) continue;
      const [mx, my] = toMap(player.x, player.y);
      if (mx < 0 || my < 0 || mx > size || my > size) continue;
      // Party members read green on the minimap as well as in the world.
      ctx.fillStyle = view.partyIds.has(player.id) ? '#8ee08a' : getClass(player.snap.cls).accent;
      ctx.beginPath();
      ctx.arc(mx, my, view.partyIds.has(player.id) ? 3.6 : 3, 0, TAU);
      ctx.fill();
    }

    if (view.navTarget) {
      const [mx, my] = toMap(view.navTarget.x, view.navTarget.y);
      // Clamped to the edge when the target is off the minimap, so it doubles
      // as a direction hint.
      const px = clamp(mx, 7, size - 7);
      const py = clamp(my, 7, size - 7);
      ctx.fillStyle = '#ffd166';
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(px, py - 6);
      ctx.lineTo(px + 6, py);
      ctx.lineTo(px, py + 6);
      ctx.lineTo(px - 6, py);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    if (me) {
      const [mx, my] = toMap(me.x, me.y);
      ctx.translate(mx, my);
      ctx.rotate(me.aim + Math.PI / 2);
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(0, -6);
      ctx.lineTo(4.5, 5);
      ctx.lineTo(0, 2.5);
      ctx.lineTo(-4.5, 5);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  /* ------------------------------------------------------------------ effects tick */

  updateEffects(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      const drag = Math.max(0, 1 - p.drag * dt);
      p.vx *= drag;
      p.vy *= drag;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    for (let i = this.texts.length - 1; i >= 0; i--) {
      const text = this.texts[i];
      text.life -= dt;
      if (text.life <= 0) {
        this.texts.splice(i, 1);
        continue;
      }
      text.y += text.vy * dt;
      text.vy *= Math.max(0, 1 - 2.2 * dt);
    }
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const ring = this.rings[i];
      ring.life -= dt;
      if (ring.life <= 0) this.rings.splice(i, 1);
    }
    for (let i = this.slashes.length - 1; i >= 0; i--) {
      const slash = this.slashes[i];
      slash.life -= dt;
      if (slash.life <= 0) this.slashes.splice(i, 1);
    }
  }

  /**
   * A neutral, mostly-transparent grain laid over the biome colours. It carries
   * no hue of its own, so it reads as texture on grass, sand and snow alike -
   * the colour comes entirely from the terrain map beneath it.
   */
  private buildGround(): void {
    const canvas = document.createElement('canvas');
    canvas.width = GROUND_SIZE;
    canvas.height = GROUND_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const cols = GROUND_SIZE / GROUND_TILE;

    for (let ty = 0; ty < cols; ty++) {
      for (let tx = 0; tx < cols; tx++) {
        const a = hash2(tx, ty, 7);
        const b = hash2(tx, ty, 31);

        // A touch of per-tile shading so large fields are not perfectly flat.
        ctx.fillStyle = `rgba(0, 0, 0, ${0.02 + a * 0.05})`;
        ctx.fillRect(tx * GROUND_TILE, ty * GROUND_TILE, GROUND_TILE, GROUND_TILE);

        if (a > 0.62) {
          ctx.fillStyle = `rgba(0, 0, 0, ${0.12 + a * 0.12})`;
          const gx = tx * GROUND_TILE + 6 + b * 16;
          const gy = ty * GROUND_TILE + 5 + a * 14;
          ctx.fillRect(gx, gy, 2, 5);
          ctx.fillRect(gx + 4, gy + 2, 2, 4);
        }
        if (b > 0.9) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.07)';
          ctx.beginPath();
          ctx.arc(tx * GROUND_TILE + 16, ty * GROUND_TILE + 16, 5 + a * 4, 0, TAU);
          ctx.fill();
        }
      }
    }

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.025)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= cols; i++) {
      ctx.beginPath();
      ctx.moveTo(i * GROUND_TILE + 0.5, 0);
      ctx.lineTo(i * GROUND_TILE + 0.5, GROUND_SIZE);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * GROUND_TILE + 0.5);
      ctx.lineTo(GROUND_SIZE, i * GROUND_TILE + 0.5);
      ctx.stroke();
    }

    this.ground = this.ctx.createPattern(canvas, 'repeat');
  }

  /**
   * Bakes the biome colours into a small canvas spanning the whole world. 512px
   * across 50,000 units is deliberately low: stretched over the viewport with
   * smoothing on, it turns Voronoi cell edges into soft transitions, and the
   * whole map costs one `drawImage` per frame instead of per-pixel work.
   */
  private buildBiomeMap(): void {
    const size = 512;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const image = ctx.createImageData(size, size);
    const data = image.data;
    const step = WORLD_SIZE / size;

    for (let py = 0; py < size; py++) {
      for (let px = 0; px < size; px++) {
        const [r, g, b] = BIOME_RGB[biomeAt((px + 0.5) * step, (py + 0.5) * step)];
        const i = (py * size + px) * 4;
        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = 255;
      }
    }

    ctx.putImageData(image, 0, 0);
    this.biomeMap = canvas;
  }

  /** Irregular stone setts for the city pavement. */
  private buildCobble(): void {
    const size = 128;
    const cell = 16;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#33363d';
    ctx.fillRect(0, 0, size, size);

    const cols = size / cell;
    for (let ty = 0; ty < cols; ty++) {
      for (let tx = 0; tx < cols; tx++) {
        const a = hash2(tx, ty, 91);
        const b = hash2(tx, ty, 53);
        const ox = (a - 0.5) * 3.5;
        const oy = (b - 0.5) * 3.5;
        const w = cell - 3.5 - a * 1.5;
        const h = cell - 3.5 - b * 1.5;
        ctx.fillStyle = `hsl(${218 + b * 22}, ${5 + a * 7}%, ${29 + a * 9}%)`;
        ctx.beginPath();
        ctx.roundRect(tx * cell + ox + 2, ty * cell + oy + 2, w, h, 4);
        ctx.fill();
        ctx.fillStyle = `hsla(210, 14%, ${48 + b * 10}%, 0.34)`;
        ctx.fillRect(tx * cell + ox + 3.5, ty * cell + oy + 3.5, w - 3, 1.8);
      }
    }
    this.cobble = this.ctx.createPattern(canvas, 'repeat');
  }
}

export type { ViewPlayer, ViewEnemy, ViewDrop };
